<script setup lang="ts">
/**
 * A zone's name, rendered as its two halves linked separately.
 *
 * Every surface that shows a zone shows `"{location}-{zone}"`, because the location half is
 * what identifies it. A card that ALSO showed the location on its own line therefore said
 * the client twice, and the obvious fix — dropping the second line — quietly cost the
 * click-to-inspect link that line carried.
 *
 * So the heading carries both instead: "Hill Inc" opens the Location, "Lebsack Group" opens
 * the Zone, and what the reader sees is unchanged. This is the component to reach for
 * anywhere a zone is named; the split itself lives in utils/zoneDisplayString.ts and must be
 * done against the location's own name, never on a hyphen (see there for why).
 *
 * The location comes from INSIDE the zone reference when the document carries one, which is
 * what lets this work on rows that have no location column — 16 of the 22 entities nesting a
 * zone — and what makes a master zone split correctly, since the sibling column there names
 * the subscriber's location rather than the master's. The `location` prop remains as a
 * fallback for documents written before that field existed.
 *
 * When the name cannot be split — a record indexed before the location prefix existed, an
 * endpoint that does not eager-load the location, a zone not named after its location — the
 * whole string renders as a single zone link. Nothing is hidden in that case; it just isn't
 * split.
 *
 * Each half is a RecordLink. It is a real link to the record's own page, so Cmd-click opens
 * a new tab. Hovering it shows the side-peek button beside it. A user who may not open that
 * page gets the old behaviour: a `<span role="button">` that opens the side peek.
 *
 * Every half must stay an INLINE box, and that is load-bearing. A <button> computes to
 * inline-block, and an inline-block or inline-flex box is ATOMIC to
 * `text-overflow: ellipsis`: the browser cannot clip inside one, so in a truncating heading
 * it drops the entire second half and renders "Windler Inc-…" with the zone name gone and
 * empty space beside it. Measured on a real card, the heading reported 11px of overflow
 * while showing barely a third of the name — the signature of a dropped atomic box rather
 * than a clipped string.
 *
 * Each half is a RecordLink in `inline` mode, whose wrapper and link are plain inline boxes.
 * The two halves therefore truncate exactly as the single string they replaced did.
 */
import { computed } from 'vue';
import RecordLink from '@/components/RecordLink.vue';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { readNestedId } from '@/utils/nestedRelation';
import { readZoneDisplayString, resolveZoneLocation, splitZoneDisplayString } from '@/utils/zoneDisplayString';

const props = withDefaults(
  defineProps<{
    /** The nested `{ id, display_string }` zone reference. */
    zone: unknown;
    /** The nested `{ id, display_string }` location reference, used to find the seam. */
    location?: unknown;
    /** Text shown when the row carries no zone at all. */
    fallback?: string;
  }>(),
  // No default for `location`: withDefaults reads a `null` default on an `unknown` prop as a
  // factory function and fails to typecheck. Undefined is what the readers already handle.
  { fallback: 'Unknown zone' },
);

const { canOpen } = useOpenRecordInInspector();

const zoneId = computed(() => readNestedId(props.zone));

/** The location to split against — see resolveZoneLocation for which one wins, and why. */
const locationRef = computed<unknown>(() => resolveZoneLocation(props.zone, props.location));

const locationId = computed(() => readNestedId(locationRef.value));

const parts = computed(() =>
  splitZoneDisplayString(readZoneDisplayString(props.zone), readZoneDisplayString(locationRef.value)),
);

/** The full name, for the `title` of whatever wraps this. */
const fullName = computed(() => {
  const { location, zone } = parts.value;
  if (zone === '') {
    return props.fallback;
  }
  return location === null ? zone : `${location}-${zone}`;
});

const zoneLabel = computed(() => (parts.value.zone === '' ? props.fallback : parts.value.zone));

const zoneInspectable = computed(() => zoneId.value !== null && parts.value.zone !== '' && canOpen('LocationZone'));
const locationInspectable = computed(() => locationId.value !== null && canOpen('Location'));

defineExpose({ fullName });
</script>

<template>
    <span :title="fullName">
        <!-- The two halves sit inside one line with the literal "-" between them, so the
             reader sees exactly the display string they would have seen unsplit. A comment
             OUTSIDE this span would make the template multi-root, which silently turns off
             attribute fallthrough and drops the title. -->
        <template v-if="parts.location !== null">
            <RecordLink
                v-if="locationInspectable"
                entity-id="Location"
                :record-id="locationId!"
                :label="`location ${parts.location}`"
                inline
                data-testid="zone-display-location-inspect"
            >{{ parts.location }}</RecordLink>
            <template v-else>{{ parts.location }}</template><!--
            -->-<!--
        --></template>
        <RecordLink
            v-if="zoneInspectable"
            entity-id="LocationZone"
            :record-id="zoneId!"
            :label="`zone ${zoneLabel}`"
            inline
            data-testid="zone-display-zone-inspect"
        >{{ zoneLabel }}</RecordLink>
        <template v-else>{{ zoneLabel }}</template>
    </span>
</template>
